// App router. With file-based routing, TanStack's Vite plugin generates
// `src/routeTree.gen.ts` from the contents of `src/routes/**`. This file
// composes it into a router instance and registers the type globally so
// platform and domain code get strict path/params/search types.

import { configureAuth, configurePodiumPlatform } from '@podium/platform/auth'
import { createRouter } from '@tanstack/react-router'

import { routeTree } from './routeTree.gen'
import { store } from './store'

// Platform boot — runs once on both server and client because router.tsx
// is imported by both server.ts and client.tsx. configurePodiumPlatform
// wires the generated SDK client (base URL + Bearer-token Authorization
// callback) and hydrates Redux from localStorage so a page reload stays
// signed in. configureAuth carries the path conventions for guards.
configureAuth()
// router.tsx runs on BOTH server and client, so the SDK base URL is resolved
// per context:
//   - SSR (Node): a relative '' has no origin to resolve against on the server,
//     so always an absolute backend address, reached directly (bypassing any
//     public tunnel). Dev default :8080; prod sets SSR_API_BASE_URL.
//   - Browser on localhost: the Vite dev server does NOT proxy /api, so call the
//     backend cross-origin at :8080 (the dev-profile CORS config allows :3000).
//   - Browser elsewhere (zrok tunnel, prod ingress): same-origin '', and the
//     proxy/ingress routes /api/* to the backend. One `pnpm dev` server is
//     reached at both hostnames, so this is decided at runtime, not baked in.
function resolveBaseUrl(): string {
  if (typeof window === 'undefined') {
    return process.env.SSR_API_BASE_URL ?? 'http://localhost:8080'
  }
  const { hostname } = window.location
  return hostname === 'localhost' || hostname === '127.0.0.1' ? 'http://localhost:8080' : ''
}
configurePodiumPlatform({ store, baseUrl: resolveBaseUrl() })

export function getRouter() {
  return createRouter({
    routeTree,
    scrollRestoration: true,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>
  }
}
